/**
 * API Response Models
 * Based on the Core DTOs from Spring Boot backend
 */

// Generic API Response DTO
export class ApiResponseDTO {
  constructor(data = {}) {
    this.success = data.success || false;
    this.message = data.message || '';
    this.data = data.data || null;
    this.errorCode = data.errorCode || data.error_code || null;
    this.timestamp = data.timestamp || new Date().toISOString();
  }

  toJSON() {
    return {
      success: this.success,
      message: this.message,
      data: this.data,
      errorCode: this.errorCode,
      timestamp: this.timestamp
    };
  }

  // Static factory methods
  static success(data, message = '') {
    return new ApiResponseDTO({
      success: true,
      data: data,
      message: message,
      timestamp: new Date().toISOString()
    });
  }

  static error(message, errorCode = null) {
    return new ApiResponseDTO({
      success: false,
      message: message,
      errorCode: errorCode,
      timestamp: new Date().toISOString()
    });
  }
}

// Pagination DTO
export class PaginationDTO {
  constructor(data = {}) {
    // Request Parameters
    this.page = data.page || 1;
    this.size = data.size || 50;
    this.sortBy = data.sortBy || data.sort_by || '';
    this.sortDirection = data.sortDirection || data.sort_direction || 'desc';
    
    // Calculated Fields
    this.totalElements = data.totalElements || data.total_elements || 0;
    this.totalPages = data.totalPages || data.total_pages || 0;
    this.hasNext = data.hasNext || data.has_next || false;
    this.hasPrevious = data.hasPrevious || data.has_previous || false;
    this.isFirst = data.isFirst || data.is_first || false;
    this.isLast = data.isLast || data.is_last || false;
  }

  toJSON() {
    return {
      page: this.page,
      size: this.size,
      sortBy: this.sortBy,
      sortDirection: this.sortDirection,
      totalElements: this.totalElements,
      totalPages: this.totalPages,
      hasNext: this.hasNext,
      hasPrevious: this.hasPrevious,
      isFirst: this.isFirst,
      isLast: this.isLast
    };
  }

  getOffset() {
    return (this.page - 1) * this.size;
  }

  calculatePaginationInfo(totalElements) {
    this.totalElements = totalElements;
    this.totalPages = Math.ceil(totalElements / this.size);
    this.hasNext = this.page < this.totalPages;
    this.hasPrevious = this.page > 1;
    this.isFirst = this.page === 1;
    this.isLast = this.page === this.totalPages;
  }
}

// Paginated Response DTO
export class PaginatedResponseDTO {
  constructor(data = {}) {
    this.content = data.content || [];
    this.pagination = data.pagination ? new PaginationDTO(data.pagination) : new PaginationDTO();
    this.success = data.success || false;
    this.message = data.message || '';
  }

  toJSON() {
    return {
      content: this.content,
      pagination: this.pagination.toJSON(),
      success: this.success,
      message: this.message
    };
  }

  // Static factory methods
  static success(content, pagination, message = '') {
    return new PaginatedResponseDTO({
      content: content,
      pagination: pagination,
      success: true,
      message: message
    });
  }

  static error(message) {
    return new PaginatedResponseDTO({
      success: false,
      message: message
    });
  }
}

// Statistics DTOs
export class CompanyStatisticsDTO {
  constructor(data = {}) {
    this.totalCompanies = data.totalCompanies || data.total_companies || 0;
    this.companiesByIndustry = data.companiesByIndustry || data.companies_by_industry || {};
    this.companiesByCountry = data.companiesByCountry || data.companies_by_country || {};
    this.companiesWithRiskAnalysis = data.companiesWithRiskAnalysis || data.companies_with_risk_analysis || 0;
    this.companiesWithoutRiskAnalysis = data.companiesWithoutRiskAnalysis || data.companies_without_risk_analysis || 0;
  }

  toJSON() {
    return {
      totalCompanies: this.totalCompanies,
      companiesByIndustry: this.companiesByIndustry,
      companiesByCountry: this.companiesByCountry,
      companiesWithRiskAnalysis: this.companiesWithRiskAnalysis,
      companiesWithoutRiskAnalysis: this.companiesWithoutRiskAnalysis
    };
  }
}

export class ApiStatisticsDTO {
  constructor(data = {}) {
    this.totalRequests = data.totalRequests || data.total_requests || 0;
    this.requestsByEndpoint = data.requestsByEndpoint || data.requests_by_endpoint || {};
    this.averageResponseTime = data.averageResponseTime || data.average_response_time || 0;
    this.errorCount = data.errorCount || data.error_count || 0;
    this.errorsByType = data.errorsByType || data.errors_by_type || {};
  }

  toJSON() {
    return {
      totalRequests: this.totalRequests,
      requestsByEndpoint: this.requestsByEndpoint,
      averageResponseTime: this.averageResponseTime,
      errorCount: this.errorCount,
      errorsByType: this.errorsByType
    };
  }
}

export class EndpointStatsDTO {
  constructor(data = {}) {
    this.endpoint = data.endpoint || '';
    this.requestCount = data.requestCount || data.request_count || 0;
    this.averageResponseTime = data.averageResponseTime || data.average_response_time || 0;
    this.errorCount = data.errorCount || data.error_count || 0;
    this.lastAccessed = data.lastAccessed || data.last_accessed || new Date().toISOString();
  }

  toJSON() {
    return {
      endpoint: this.endpoint,
      requestCount: this.requestCount,
      averageResponseTime: this.averageResponseTime,
      errorCount: this.errorCount,
      lastAccessed: this.lastAccessed
    };
  }
}

export class UserStatsDTO {
  constructor(data = {}) {
    this.userId = data.userId || data.user_id || '';
    this.totalRequests = data.totalRequests || data.total_requests || 0;
    this.requestsByEndpoint = data.requestsByEndpoint || data.requests_by_endpoint || {};
    this.lastActive = data.lastActive || data.last_active || new Date().toISOString();
    this.watchlistItems = data.watchlistItems || data.watchlist_items || 0;
  }

  toJSON() {
    return {
      userId: this.userId,
      totalRequests: this.totalRequests,
      requestsByEndpoint: this.requestsByEndpoint,
      lastActive: this.lastActive,
      watchlistItems: this.watchlistItems
    };
  }
}

export class HourlyStatsDTO {
  constructor(data = {}) {
    this.hour = data.hour || new Date().toISOString();
    this.requestCount = data.requestCount || data.request_count || 0;
    this.averageResponseTime = data.averageResponseTime || data.average_response_time || 0;
    this.errorCount = data.errorCount || data.error_count || 0;
    this.uniqueUsers = data.uniqueUsers || data.unique_users || 0;
  }

  toJSON() {
    return {
      hour: this.hour,
      requestCount: this.requestCount,
      averageResponseTime: this.averageResponseTime,
      errorCount: this.errorCount,
      uniqueUsers: this.uniqueUsers
    };
  }
}

export class FirebaseCostSummaryDTO {
  constructor(data = {}) {
    this.totalReads = data.totalReads || data.total_reads || 0;
    this.totalWrites = data.totalWrites || data.total_writes || 0;
    this.estimatedCost = data.estimatedCost || data.estimated_cost || 0;
    this.period = data.period || '';
    this.readsByCollection = data.readsByCollection || data.reads_by_collection || {};
    this.writesByCollection = data.writesByCollection || data.writes_by_collection || {};
  }

  toJSON() {
    return {
      totalReads: this.totalReads,
      totalWrites: this.totalWrites,
      estimatedCost: this.estimatedCost,
      period: this.period,
      readsByCollection: this.readsByCollection,
      writesByCollection: this.writesByCollection
    };
  }
}

// Model Factory Functions
export const createApiResponse = (data) => new ApiResponseDTO(data);
export const createPagination = (data) => new PaginationDTO(data);
export const createPaginatedResponse = (data) => new PaginatedResponseDTO(data);
export const createCompanyStatistics = (data) => new CompanyStatisticsDTO(data);
export const createApiStatistics = (data) => new ApiStatisticsDTO(data);
export const createEndpointStats = (data) => new EndpointStatsDTO(data);
export const createUserStats = (data) => new UserStatsDTO(data);
export const createHourlyStats = (data) => new HourlyStatsDTO(data);
export const createFirebaseCostSummary = (data) => new FirebaseCostSummaryDTO(data);
